// ============================================================
// Shared design tokens for the multi-role portals.
// Matches the existing app's gold / navy design language so the
// new Owner (Property Management), Tenant, Guest and EA screens
// feel native to the rest of InvestMenter.
// ============================================================

export const theme = {
  gold: '#D6B160',
  goldDark: '#c4a555',
  navy: '#1a1a2e',
  ink: '#111827',
  slate: '#6b7280',
  slateLight: '#9ca3af',
  line: '#e5e7eb',
  bg: '#f7f8fa',
  card: '#ffffff',
  success: '#16a34a',
  successBg: '#dcfce7',
  warning: '#d97706',
  warningBg: '#fef3c7',
  danger: '#dc2626',
  dangerBg: '#fee2e2',
  info: '#2563eb',
  infoBg: '#dbeafe',
  shadow: '0 2px 12px rgba(0,0,0,0.06)',
  shadowLg: '0 8px 28px rgba(0,0,0,0.10)',
  radius: 16,
  radiusSm: 12,
};

// 'owner' is the Property Owner (investor) role — the id stays 'owner' so
// existing storage / analytics keep working; only its label changed.
// 'guest' is the Airbnb short-stay guest (booking), NOT a prospect preview —
// a prospect who wants to look at the product takes 'demo' instead.
// 'ceo' is the company-side executive role backed by the CRM SaaS client portal.
// 'demo' is the login-free Asset Desk tour on bundled sample data.
// 'partner' is the Cooperation / Referral Partner area. It is not a separate
// account type — it signs in with the same InvestMenter account as 'owner'.
export type AppRole = 'owner' | 'tenant' | 'guest' | 'ceo' | 'ea' | 'demo' | 'partner';

// localStorage key that remembers the chosen role across launches.
export const ROLE_STORAGE_KEY = 'appRole';

// ------------------------------------------------------------
// Staff / EA detection (frontend placeholder).
// Real detection will come from the login response (e.g. an
// `isStaff` / `isAdmin` flag on the account). Until backend is
// wired, we recognise staff by email so the Admin overview can be
// demoed on the mock Tenant / Guest logins.
// ------------------------------------------------------------
// ------------------------------------------------------------
// Who opens on the ADMIN overview instead of an owner dashboard.
//
// The real answer comes from the login response (`isAdmin`, computed on the
// server from ADMIN_PORTAL_EMAILS / PM_ADMIN_EMAILS — see server.js). This
// list is the second half of an OR, for the two cases where that flag is not
// available or not yet true:
//
//   • a restored session, which has an investor record but no login response;
//   • a build running against a backend that predates the flag.
//
// It is NOT a security boundary and cannot be one: it only decides which
// screen renders, for an account the person has already signed into. Every
// endpoint the admin screens call re-checks the caller on the server, against
// the same PM_ADMIN_EMAILS list. Deliberately NOT `isStaffEmail` below — that
// matches the whole @investmenter.de domain, and a colleague in sales opening
// their own portfolio should not land in the property-management console.
// ------------------------------------------------------------
export const PM_ADMIN_EMAILS = [
  'm.soriano@investmenter.de', 'j.reyes@investmenter.de',
  'g.joloc@investmenter.de', 'j.ricaro@investmenter.de', 'k.alvarez@investmenter.de',
];

export const isAdminAccount = (email?: string | null): boolean => {
  const e = (email || '').trim().toLowerCase();
  return !!e && PM_ADMIN_EMAILS.includes(e);
};

export const isStaffEmail = (email: string): boolean => {
  const e = (email || '').trim().toLowerCase();
  if (!e) return false;
  return (
    e.endsWith('@investmenter.de') ||
    e.startsWith('ea@') ||
    e.startsWith('staff@') ||
    e.startsWith('admin@') ||
    e.includes('marilyn')
  );
};
